import { useState, useCallback } from 'react'

const storageTypes = ['sessionStorage', 'localStorage']

export type StorageValueType = null | number | string
export type StorageType = 'sessionStorage' | 'localStorage'

export const useStorage = (
    key: string,
    initialValue: StorageValueType,
    storage: StorageType = 'sessionStorage'
) => {
    if (!storageTypes.includes(storage)) {
        throw new Error(`Storage available variable types: ${storageTypes.join(', ')}`)
    }
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        try {
            // Get from local storage by key
            const item = window?.[storage]?.getItem(key)
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue
        } catch (error) {
            // If error also return initialValue
            console.error(error)
            return initialValue
        }
    })
    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = useCallback(
        (value: StorageValueType) => {
            try {
                // Save state
                setStoredValue(value)
                // Save to local storage
                window?.[storage]?.setItem(key, JSON.stringify(value))
            } catch (error) {
                // A more advanced implementation would handle the error case
                console.error(error)
            }
        },
        [key, storage]
    )

    return [storedValue, setValue]
}
